import { useState } from "react";
import { api } from "../api";
import { useAsync } from "../hooks";

/** GET /me/usage — this period's billable usage + the plan's limits. */
type UsageDTO = {
  plan: string;
  planLabel: string;
  /** First day of the billing month (YYYY-MM-DD). */
  period: string;
  successfulRuns: number;
  includedRuns: number;
  scrapers: number;
  scraperCap: number;
  /** Stripe configured → show upgrade / manage-billing actions. */
  billingEnabled: boolean;
};

/** A limit is "unlimited" (enterprise/internal) when it's an astronomically large cap. */
const isUnlimited = (n: number): boolean => n >= 1e9;

function monthLabel(period: string): string {
  const d = new Date(period);
  return isNaN(d.getTime()) ? period : d.toLocaleString(undefined, { month: "long", year: "numeric" });
}

/**
 * Plan & usage — the customer's current tier + this month's usage against its limits. Reads GET /me/usage.
 * The meter is SUCCESSFUL runs only: failed and self-healed runs don't count, so a customer never pays
 * because our selectors broke. Upgrades (Stripe) are a later phase — for now this is read-only + a nudge.
 */
export function Usage() {
  const { data, loading, error } = useAsync<UsageDTO>(() => api("/me/usage"), []);
  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState<string | null>(null);

  // Kick off a Stripe Checkout / Portal session and hand the browser to Stripe's hosted page.
  async function go(path: string, body?: unknown) {
    setBusy(true);
    setActionErr(null);
    try {
      const res = await api<{ url: string }>(path, {
        method: "POST",
        body: body ? JSON.stringify(body) : undefined,
      });
      window.location.href = res.url;
    } catch (e) {
      setActionErr(e instanceof Error ? e.message : "billing action failed");
      setBusy(false);
    }
  }

  if (loading && !data) return <div className="muted pad">Loading…</div>;
  if (error) return <div className="err pad">Error: {error}</div>;
  if (!data) return null;

  return (
    <div className="stack">
      <div className="section-head">
        <h1>Plan &amp; usage</h1>
      </div>
      <p className="muted" style={{ marginTop: 0, maxWidth: 620 }}>
        Your plan and this month&rsquo;s usage. Only <b>successful</b> runs count — a run that failed or that
        Amendie repaired for you is free.
      </p>

      <section
        style={{
          border: "1px solid var(--rule)",
          borderRadius: "var(--r)",
          padding: "var(--sp4)",
        }}
      >
        <div className="row-gap" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
          <div>
            <b>{data.planLabel}</b> plan
          </div>
          <span className="muted sm">{monthLabel(data.period)}</span>
        </div>

        <Meter label="Successful runs" used={data.successfulRuns} cap={data.includedRuns} unit="runs" />
        <Meter label="Scrapers" used={data.scrapers} cap={data.scraperCap} unit="scrapers" />
      </section>

      {data.billingEnabled && data.plan === "free" && (
        <div className="row-gap">
          <button className="btn" disabled={busy} onClick={() => void go("/billing/checkout", { plan: "pro" })}>
            Upgrade to Pro
          </button>
          <button
            className="btn ghost"
            disabled={busy}
            onClick={() => void go("/billing/checkout", { plan: "business" })}
          >
            Upgrade to Business
          </button>
        </div>
      )}
      {data.billingEnabled && (data.plan === "pro" || data.plan === "business") && (
        <button className="btn ghost sm" disabled={busy} onClick={() => void go("/billing/portal")}>
          Manage billing
        </button>
      )}
      {!data.billingEnabled && data.plan !== "enterprise" && (
        <p className="muted sm" style={{ maxWidth: 620 }}>
          Need more headroom? Self-service upgrades are coming soon.
        </p>
      )}
      {actionErr && <div className="err sm">{actionErr}</div>}
    </div>
  );
}

function Meter({ label, used, cap, unit }: { label: string; used: number; cap: number; unit: string }) {
  const unlimited = isUnlimited(cap);
  const over = !unlimited && used >= cap;
  const pct = unlimited ? 0 : Math.min(100, (used / Math.max(1, cap)) * 100);
  return (
    <div style={{ marginTop: "var(--sp4)" }}>
      <div className="row-gap sm" style={{ justifyContent: "space-between" }}>
        <span>{label}</span>
        <span className={over ? "gold" : "muted"}>
          {used.toLocaleString()}
          {unlimited ? "" : ` / ${cap.toLocaleString()}`} {unit}
          {unlimited ? " · unlimited" : ""}
        </span>
      </div>
      {!unlimited && (
        <div
          style={{ height: 6, borderRadius: 3, background: "var(--wash)", marginTop: 5, overflow: "hidden" }}
          role="progressbar"
          aria-valuenow={Math.round(pct)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div style={{ width: `${pct}%`, height: "100%", background: over ? "var(--crack)" : "var(--ink)" }} />
        </div>
      )}
    </div>
  );
}
